// src/components/OfferForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; 
import { db, storage } from '../utils/firebaseConfig';
import { collection, addDoc, getDocs, query, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import GeneralNavbar from './GeneralNavbar';
import './OfferForm.css';
import { getNextSharedId } from '../utils/counterService';
import { cacheService, CACHE_KEYS } from '../utils/cacheService';

const OfferForm = () => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [stock, setStock] = useState('');
  const [category, setCategory] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [discount, setDiscount] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [categories, setCategories] = useState([]); // Lista de categorías
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate(); 

  const handleBackClick = () => {
    navigate('/admin'); 
  };

  // 🔹 Cargar categorías al montar el componente
  useEffect(() => {
    loadCategories();
  }, []);

  // 🔹 Cargar lista de categorías (con caché)
  const loadCategories = async () => {
    try {
      setLoading(true);
      
      // 🚀 Verificar caché primero
      const cached = cacheService.get(CACHE_KEYS.CATEGORIES);
      if (cached) {
        setCategories(cached);
        setLoading(false);
        return;
      }

      // Si no hay caché, cargar de Firebase
      const q = query(collection(db, 'categories'), orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      
      const categoryList = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name
      }));
      
      // 💾 Guardar en caché
      cacheService.set(CACHE_KEYS.CATEGORIES, categoryList);
      
      setCategories(categoryList);
      setLoading(false);
    } catch (error) {
      console.error("Error cargando categorías:", error);
      setLoading(false);
    }
  };

  // Función para subir la imagen a Firebase Storage y obtener la URL
  const handleImageUpload = async (file) => {
    if (!file) return null;

    const storageRef = ref(storage, `offer-images/${file.name}_${Date.now()}`);
    await uploadBytes(storageRef, file);
    const imageUrl = await getDownloadURL(storageRef);
    return imageUrl;
  };

  // Función para manejar el envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    // Validar que el descuento no exceda el 100%
    if (discount && (parseFloat(discount) < 0 || parseFloat(discount) > 100)) {
      alert('El descuento debe estar entre 0% y 100%.');
      return;
    }

    let imageUrl = '';
    if (imageFile) {
      imageUrl = await handleImageUpload(imageFile);
    }

    const offerId = await getNextSharedId(); // Usar el contador compartido

    const offerData = {
      id: offerId,
      name,
      description,
      price: parseFloat(price),
      stock: parseInt(stock),
      category,
      discount: discount ? parseFloat(discount) : 0,
      enabled,
      image: imageUrl,
      isCombo: true,
    };
  
    try {
      const offerCollectionRef = collection(db, 'offers');
      await addDoc(offerCollectionRef, offerData);
      alert('Oferta agregada correctamente');
  
      // Limpiar los campos después de agregar la oferta
      setName('');
      setDescription('');
      setPrice('');
      setStock('');
      setCategory('');
      setDiscount(''); 
      setImageFile(null);
      setEnabled(true);
    } catch (error) {
      console.error('Error al registrar la oferta: ', error);
      alert('Hubo un error al agregar la oferta. Inténtalo de nuevo.');
    }
  };
  

  return (
    <>
      <GeneralNavbar title="Agregar Combo" onBackClick={handleBackClick} />
      <div className="offer-form-wrapper">
        <div className="offer-form-container">
          <form onSubmit={handleSubmit}>

            <div className="offer-form-group">
              <label className="offer-form-label">Título del Combo:</label>
              <input
                type="text"
                className="offer-form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            {/* Campo de descripción */}
            <div className="offer-form-group">
              <label className="offer-form-label">Descripción:</label>
              <textarea
                className="offer-form-input"
                value={description} 
                onChange={(e) => setDescription(e.target.value)} 
                required 
              />
            </div>
            <div className="offer-form-group">
              <label className="offer-form-label">Precio Original:</label>
              <input 
                type="number" 
                className="offer-form-input"
                value={price} 
                onChange={(e) => setPrice(e.target.value)} 
                min="0"
                step="0.01"
                required 
              />
            </div>
            <div className="offer-form-group">
              <label className="offer-form-label">Stock:</label>
              <input 
                type="number" 
                className="offer-form-input"
                value={stock} 
                onChange={(e) => setStock(e.target.value)} 
                min="0"
                required 
              />
            </div>
            
             {/* 🔹 Menú desplegable para Categoría */}
            <div className="offer-form-group">
              <label className="offer-form-label">Categoría:</label>
              <select 
                className="offer-form-input"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                required
                disabled={loading}
              >
                <option value="">Selecciona una categoría</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Campo para porcentaje de descuento */}
            <div className="offer-form-group">
              <label className="offer-form-label">Descuento (%):</label>
              <input 
                type="number" 
                className="offer-form-input"
                value={discount} 
                onChange={(e) => setDiscount(e.target.value)} 
                min="0" 
                max="100" 
              />
            </div>
            {/* Campo para activar/desactivar la oferta */}
            <div className="offer-form-group">
              <label className="offer-form-label">Estado:</label>
              <select
                className="offer-form-input"
                value={enabled}
                onChange={(e) => setEnabled(e.target.value === 'true')}
              >
                <option value="true">Activo</option>
                <option value="false">Inactivo</option>
              </select>
            </div>
            <div className="offer-form-group">
              <label className="offer-form-label">Imagen:</label>
              <input 
                type="file" 
                className="offer-form-input"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files[0])} 
              />
            </div>
            <button type="submit" className="offer-form-button">
              Agregar Oferta
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default OfferForm;
